// Typed fetch wrapper for every route in docs/API_CONTRACT.md.
// Real requests are used when VITE_USE_MOCK=false; otherwise every call is served by
// api/mock.ts's in-memory implementation, so the app is fully demoable with no backend.

// Review fix F15 types -- a separate import so this change doesn't touch the shared list below.
import type { ClaimReinforcementBody, ReinforcementStatus } from './types';
import { config } from '../config';
import { refreshIdToken } from '../auth/cognito';
import { restoreSession } from '../auth/cognitoSrp';
import { getIdToken, getRefreshToken, setIdToken } from '../auth/token';
// Demo data loads on demand so it never ships in the production (non-mock) bundle's main chunk.
const loadMock = () => import('./mock');
import type { Tradition } from '../theme/tokens';
import type {
  AdminAnalytics,
  AdminUser,
  AdminUsersResponse,
  BillingStatus,
  CalendarResponse,
  CreateCheckoutResponse,
  CreateJournalEntryBody,
  DeleteMeResponse,
  JournalEntriesResponse,
  JournalEntry,
  LeaderboardResponse,
  LibraryDownload,
  LibraryResponse,
  Me,
  PillarsResponse,
  PracticeAudio,
  PracticesResponse,
  PracticeType,
  PostSessionBody,
  PostSessionResponse,
  ReinforcementResponse,
  Routine,
  ThirtyDaySchedule,
  Tier,
  TodayPlan,
  TodayQuotes,
  UpdateJournalEntryBody,
  UpdateMeBody,
  UpdateRoutineBody,
} from './types';
import type { Locale, PublicSampleResponse } from './types';

class ApiError extends Error {
  status: number;
  /** Present on a 403 from the content-access gate (see PracticeAccess) -- lets callers branch
   * on the precise reason instead of parsing the human-readable message text. */
  access?: string;
  constructor(status: number, message: string, access?: string) {
    super(message);
    this.status = status;
    this.access = access;
    this.name = 'ApiError';
  }
}

async function doFetch(path: string, init: RequestInit, token: string | null): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return fetch(`${config.apiBaseUrl}${path}`, { ...init, headers });
}

/** Tries every way this app might be able to silently mint a fresh ID token:
 * the SRP sign-in path caches its session inside amazon-cognito-identity-js
 * itself (restoreSession), while the Google/Hosted-UI path only ever gave us
 * a raw refresh_token to hang onto ourselves (auth/token.ts). Returns null if
 * neither works, meaning the user genuinely needs to sign in again. */
async function silentlyRefreshIdToken(): Promise<string | null> {
  const viaSrpSession = await restoreSession().catch(() => null);
  if (viaSrpSession) return viaSrpSession;

  const storedRefreshToken = getRefreshToken();
  if (storedRefreshToken) {
    const viaRefreshToken = await refreshIdToken(storedRefreshToken).catch(() => null);
    if (viaRefreshToken) return viaRefreshToken;
  }

  return null;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let res = await doFetch(path, init, getIdToken());

  // The Cognito ID token is only valid for ~1 hour; a long-lived tab hits this
  // constantly. Rather than silently failing every call (and every save)
  // once it expires, try a silent refresh and retry once before giving up.
  if (res.status === 401) {
    const refreshed = await silentlyRefreshIdToken();
    if (refreshed) {
      setIdToken(refreshed);
      res = await doFetch(path, init, refreshed);
    }
  }

  if (!res.ok) {
    let message = res.statusText;
    let access: string | undefined;
    try {
      const body = (await res.json()) as { error?: string; access?: string };
      if (body?.error) message = body.error;
      access = body?.access;
    } catch {
      // response body wasn't JSON -- keep statusText
    }
    throw new ApiError(res.status, message, access);
  }

  return (await res.json()) as T;
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  const search = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${search.toString()}`;
}

/** Unauthenticated calls for the public website (review fix F11). Deliberately separate from `api`
 * below: no Authorization header, no token-refresh retry, and no Content-Type on a GET -- so the
 * browser sends a simple request (no CORS preflight) to the one route that has no JWT authorizer. */
export const publicApi = {
  async getPublicSample(lang?: Locale): Promise<PublicSampleResponse> {
    if (config.useMock) return (await loadMock()).mockPublicApi.getPublicSample(lang);
    const res = await fetch(`${config.apiBaseUrl}/api/public/sample${qs({ lang })}`);
    if (!res.ok) {
      let message = res.statusText;
      try {
        const body = (await res.json()) as { error?: string };
        if (body?.error) message = body.error;
      } catch {
        // non-JSON error body -- keep statusText
      }
      throw new ApiError(res.status, message);
    }
    return (await res.json()) as PublicSampleResponse;
  },
};

export const api = {
  async getMe(): Promise<Me> {
    if (config.useMock) return (await loadMock()).mockApi.getMe();
    return request<Me>('/api/me');
  },

  async updateMe(body: UpdateMeBody): Promise<Me> {
    if (config.useMock) return (await loadMock()).mockApi.updateMe(body);
    return request<Me>('/api/me', { method: 'PUT', body: JSON.stringify(body) });
  },

  async getTodayQuotes(): Promise<TodayQuotes> {
    if (config.useMock) return (await loadMock()).mockApi.getTodayQuotes();
    return request<TodayQuotes>('/api/quotes/today');
  },

  async getPractices(params: { type?: PracticeType; tradition?: Tradition } = {}): Promise<PracticesResponse> {
    if (config.useMock) return (await loadMock()).mockApi.getPractices(params);
    return request<PracticesResponse>(`/api/practices${qs(params)}`);
  },

  async getPillars(params: { tradition?: Tradition; all?: boolean } = {}): Promise<PillarsResponse> {
    if (config.useMock) return (await loadMock()).mockApi.getPillars(params);
    return request<PillarsResponse>(`/api/pillars${qs({ tradition: params.tradition, all: params.all ? 'true' : undefined })}`);
  },

  async getCalendar(params: { tradition?: Tradition } = {}): Promise<CalendarResponse> {
    if (config.useMock) return (await loadMock()).mockApi.getCalendar(params);
    return request<CalendarResponse>(`/api/calendar${qs(params)}`);
  },

  async getPracticeAudio(practiceId: string): Promise<PracticeAudio> {
    if (config.useMock) return (await loadMock()).mockApi.getPracticeAudio(practiceId);
    return request<PracticeAudio>(`/api/practices/${encodeURIComponent(practiceId)}/audio`);
  },

  async getLibrary(tradition?: Tradition): Promise<LibraryResponse> {
    if (config.useMock) return (await loadMock()).mockApi.getLibrary(tradition);
    return request<LibraryResponse>(`/api/library${qs({ tradition })}`);
  },

  async getLibraryDownload(bookId: string): Promise<LibraryDownload> {
    if (config.useMock) return (await loadMock()).mockApi.getLibraryDownload(bookId);
    return request<LibraryDownload>(`/api/library/${encodeURIComponent(bookId)}/download`);
  },

  async postSession(body: PostSessionBody): Promise<PostSessionResponse> {
    if (config.useMock) return (await loadMock()).mockApi.postSession(body);
    return request<PostSessionResponse>('/api/sessions', { method: 'POST', body: JSON.stringify(body) });
  },

  async getLeaderboard(limit = 20): Promise<LeaderboardResponse> {
    if (config.useMock) return (await loadMock()).mockApi.getLeaderboard(limit);
    return request<LeaderboardResponse>(`/api/leaderboard${qs({ limit })}`);
  },

  /** Claims one Streak Shield for a completed occurrence of today's assigned reinforcement (see
   * getReinforcementStatus). `sessionId` is optional -- omitted, the server finds today's
   * unclaimed occurrence of the assignment itself. */
  async postReinforcement(body: ClaimReinforcementBody = {}): Promise<ReinforcementResponse> {
    if (config.useMock) return (await loadMock()).mockApi.postReinforcement(body);
    return request<ReinforcementResponse>('/api/reinforcements', { method: 'POST', body: JSON.stringify(body) });
  },

  async getReinforcementStatus(): Promise<ReinforcementStatus> {
    if (config.useMock) return (await loadMock()).mockApi.getReinforcementStatus();
    return request<ReinforcementStatus>('/api/reinforcements');
  },

  async createCheckout(): Promise<CreateCheckoutResponse> {
    if (config.useMock) return (await loadMock()).mockApi.createCheckout();
    return request<CreateCheckoutResponse>('/api/billing/checkout', { method: 'POST' });
  },

  async getBillingStatus(): Promise<BillingStatus> {
    if (config.useMock) return (await loadMock()).mockApi.getBillingStatus();
    return request<BillingStatus>('/api/billing/status');
  },

  async getToday(): Promise<TodayPlan> {
    if (config.useMock) return (await loadMock()).mockApi.getToday();
    return request<TodayPlan>('/api/today');
  },

  async getSchedule(pillarId: string): Promise<ThirtyDaySchedule> {
    if (config.useMock) return (await loadMock()).mockApi.getSchedule(pillarId);
    return request<ThirtyDaySchedule>(`/api/schedule${qs({ pillarId })}`);
  },

  async getRoutine(): Promise<Routine> {
    if (config.useMock) return (await loadMock()).mockApi.getRoutine();
    return request<Routine>('/api/routine');
  },

  async updateRoutine(body: UpdateRoutineBody): Promise<Routine> {
    if (config.useMock) return (await loadMock()).mockApi.updateRoutine(body);
    return request<Routine>('/api/routine', { method: 'PUT', body: JSON.stringify(body) });
  },

  async deleteRoutine(): Promise<Routine> {
    if (config.useMock) return (await loadMock()).mockApi.deleteRoutine();
    return request<Routine>('/api/routine', { method: 'DELETE' });
  },

  /** One cursor-paginated page, newest first. Pass the previous page's `nextCursor` as
   * `cursor` for the next one. */
  async listJournalEntries(params: { cursor?: string; limit?: number } = {}): Promise<JournalEntriesResponse> {
    if (config.useMock) return (await loadMock()).mockApi.listJournalEntries(params);
    return request<JournalEntriesResponse>(`/api/journal${qs(params)}`);
  },

  async createJournalEntry(body: CreateJournalEntryBody): Promise<JournalEntry> {
    if (config.useMock) return (await loadMock()).mockApi.createJournalEntry(body);
    return request<JournalEntry>('/api/journal', { method: 'POST', body: JSON.stringify(body) });
  },

  async updateJournalEntry(entryId: string, body: UpdateJournalEntryBody): Promise<JournalEntry> {
    if (config.useMock) return (await loadMock()).mockApi.updateJournalEntry(entryId, body);
    return request<JournalEntry>(`/api/journal/${encodeURIComponent(entryId)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async deleteJournalEntry(entryId: string): Promise<{ deleted: boolean }> {
    if (config.useMock) return (await loadMock()).mockApi.deleteJournalEntry(entryId);
    return request<{ deleted: boolean }>(`/api/journal/${encodeURIComponent(entryId)}`, { method: 'DELETE' });
  },

  /** One byte-bounded export chunk. Use exportAllJournalEntries to get the complete export. */
  async exportJournal(cursor?: string): Promise<JournalEntriesResponse> {
    if (config.useMock) return (await loadMock()).mockApi.exportJournal(cursor);
    return request<JournalEntriesResponse>(`/api/journal/export${qs({ cursor })}`);
  },

  /** The complete export: follows every chunk's `nextCursor` until the server says there's no
   * more, de-duplicating by entryId as a belt-and-braces guard. Each chunk is bounded server-side
   * to stay under the Lambda response-size limit, so a large journal arrives in several requests
   * rather than failing or silently truncating. */
  async exportAllJournalEntries(onProgress?: (count: number) => void): Promise<JournalEntry[]> {
    const seen = new Set<string>();
    const all: JournalEntry[] = [];
    let cursor: string | undefined;
    for (let chunk = 0; chunk < 10_000; chunk++) {
      const res = await api.exportJournal(cursor);
      for (const entry of res.entries) {
        if (seen.has(entry.entryId)) continue;
        seen.add(entry.entryId);
        all.push(entry);
      }
      onProgress?.(all.length);
      if (!res.nextCursor) return all;
      cursor = res.nextCursor;
    }
    throw new Error('Export did not finish -- please try again.');
  },

  /** DELETE /api/me: deletes the caller's journal, profile/routine/progress and sign-in account. */
  async deleteAccount(): Promise<DeleteMeResponse> {
    if (config.useMock) return (await loadMock()).mockApi.deleteAccount();
    return request<DeleteMeResponse>('/api/me', { method: 'DELETE' });
  },

  async getAdminUsers(q?: string): Promise<AdminUsersResponse> {
    if (config.useMock) return (await loadMock()).mockApi.getAdminUsers(q);
    return request<AdminUsersResponse>(`/api/admin/users${qs({ q })}`);
  },

  /** `adminPremiumGrant` sets only the admin source of Premium (never a paid membership);
   * `{ tier }` is the backward-compatible alias for it. */
  async updateAdminUser(sub: string, body: { adminPremiumGrant: boolean } | { tier: Tier }): Promise<AdminUser> {
    if (config.useMock) return (await loadMock()).mockApi.updateAdminUser(sub, body);
    return request<AdminUser>(`/api/admin/users/${encodeURIComponent(sub)}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  },

  async getAdminAnalytics(): Promise<AdminAnalytics> {
    if (config.useMock) return (await loadMock()).mockApi.getAdminAnalytics();
    return request<AdminAnalytics>('/api/admin/analytics');
  },
};

export { ApiError };
